import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface MealOptionItem {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface TicketTierItem {
  id: string;
  name: string;
  price: number | string;
  description: string;
  active: boolean;
}

export interface RegistrationRow {
  id: string;
  userId?: string | null;
  fullName: string;
  email: string | null;
  status: 'Confirmed' | 'Cancelled';
  dietaryPrefs: string | null;
  chapterAffiliation: string | null;
  mealSelection: { mealOptionId: string; mealOptionName: string } | null;
  seatAssignment: { tableNumber: number; seatNumber: number } | null;
  ticket: { tierName: string; priceAtPurchase: number; ticketNumber?: number } | null;
  source?: 'registration' | 'guest-record';
}

interface RegistrationEditModalProps {
  registration: RegistrationRow;
  mealOptions: MealOptionItem[];
  ticketTiers: TicketTierItem[];
  onClose: () => void;
  onSaved: (updated: RegistrationRow) => void;
}

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  content: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  header: {
    marginBottom: '1.25rem',
    borderBottom: '1px solid #e0e0e0',
    paddingBottom: '0.75rem',
  },
  name: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#0f2d1f',
    margin: 0,
  },
  email: {
    fontSize: '0.85rem',
    color: '#666',
    margin: '0.25rem 0 0 0',
  },
  fieldGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
    marginBottom: '0.3rem',
  },
  select: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '1.25rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #e0e0e0',
  },
  saveBtn: {
    padding: '0.5rem 1.25rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  saveBtnDisabled: {
    padding: '0.5rem 1.25rem',
    backgroundColor: '#ccc',
    color: '#666',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  cancelBtn: {
    padding: '0.5rem 1.25rem',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
};

export function RegistrationEditModal({
  registration,
  mealOptions,
  ticketTiers,
  onClose,
  onSaved,
}: RegistrationEditModalProps) {
  const [status, setStatus] = useState<'Confirmed' | 'Cancelled'>(registration.status);
  const [dietaryPrefs, setDietaryPrefs] = useState(registration.dietaryPrefs ?? '');
  const [chapterAffiliation, setChapterAffiliation] = useState(registration.chapterAffiliation ?? '');
  const [chapters, setChapters] = useState<{ id: string; name: string }[]>([]);
  const [mealOptionId, setMealOptionId] = useState(registration.mealSelection?.mealOptionId ?? '');
  const [tierId, setTierId] = useState(() => {
    const matchingTier = ticketTiers.find((t) => t.name === registration.ticket?.tierName);
    return matchingTier?.id ?? '';
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch chapters for dropdown
  useEffect(() => {
    apiClient<{ id: string; name: string }[]>('/chapters')
      .then((data) => setChapters(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  async function handleSave() {
    setSaving(true);
    setError('');

    try {
      let updatedRegistration = { ...registration };

      // 1. Check if registration-level fields changed
      const registrationChanged =
        status !== registration.status ||
        dietaryPrefs !== (registration.dietaryPrefs ?? '') ||
        chapterAffiliation !== (registration.chapterAffiliation ?? '');

      if (registrationChanged) {
        const patchBody: Record<string, string> = {};
        if (status !== registration.status) patchBody.status = status;
        if (dietaryPrefs !== (registration.dietaryPrefs ?? '')) patchBody.dietaryPrefs = dietaryPrefs;
        if (chapterAffiliation !== (registration.chapterAffiliation ?? '')) patchBody.chapterAffiliation = chapterAffiliation;

        const result = await apiClient<{ id: string; status: string; dietaryPrefs: string | null; chapterAffiliation: string | null }>(
          `/legacy-ball/registrations/${registration.id}`,
          { method: 'PATCH', body: patchBody },
        );

        updatedRegistration = {
          ...updatedRegistration,
          status: result.status as 'Confirmed' | 'Cancelled',
          dietaryPrefs: result.dietaryPrefs,
          chapterAffiliation: result.chapterAffiliation,
        };
      }

      // 2. Check if meal selection changed
      const originalMealId = registration.mealSelection?.mealOptionId ?? '';
      if (mealOptionId !== originalMealId && mealOptionId) {
        await apiClient('/legacy-ball/meal-selections', {
          method: 'POST',
          body: { registrationId: registration.id, mealOptionId },
        });
        const mealName = mealOptions.find((m) => m.id === mealOptionId)?.name ?? '';
        updatedRegistration = {
          ...updatedRegistration,
          mealSelection: { mealOptionId, mealOptionName: mealName },
        };
      }

      // 3. Check if ticket tier changed
      const originalTierId = ticketTiers.find((t) => t.name === registration.ticket?.tierName)?.id ?? '';
      if (tierId !== originalTierId && tierId) {
        const ticketResult = await apiClient<{ tierName: string; priceAtPurchase: number | string }>(
          '/legacy-ball/tickets',
          { method: 'POST', body: { registrationId: registration.id, tierId } },
        );
        updatedRegistration = {
          ...updatedRegistration,
          ticket: { tierName: ticketResult.tierName, priceAtPurchase: Number(ticketResult.priceAtPurchase) },
        };
      }

      onSaved(updatedRegistration);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.content} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={modalStyles.header}>
          <p style={modalStyles.name}>{registration.fullName}</p>
          <p style={modalStyles.email}>
            {registration.email}
            {registration.userId && (
              <a
                href={`/users?highlight=${registration.userId}`}
                style={{ marginLeft: '8px', fontSize: '0.8rem', color: '#1a6b3c', textDecoration: 'none' }}
                title="Edit user profile (email, name, role)"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = `/users?highlight=${registration.userId}`;
                }}
              >
                ✏️ Edit User Profile
              </a>
            )}
          </p>
        </div>

        {error && <div style={modalStyles.error}>{error}</div>}

        {/* Status */}
        <div style={modalStyles.fieldGroup}>
          <label style={modalStyles.label}>Status</label>
          <select
            style={modalStyles.select}
            value={status}
            onChange={(e) => setStatus(e.target.value as 'Confirmed' | 'Cancelled')}
          >
            <option value="Confirmed">Confirmed</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>

        {/* Meal Selection */}
        <div style={modalStyles.fieldGroup}>
          <label style={modalStyles.label}>Meal Selection</label>
          <select
            style={modalStyles.select}
            value={mealOptionId}
            onChange={(e) => setMealOptionId(e.target.value)}
          >
            <option value="">No meal selected</option>
            {mealOptions.map((meal) => (
              <option key={meal.id} value={meal.id}>
                {meal.name}
              </option>
            ))}
          </select>
        </div>

        {/* Ticket Tier */}
        <div style={modalStyles.fieldGroup}>
          <label style={modalStyles.label}>Ticket Tier</label>
          <select
            style={modalStyles.select}
            value={tierId}
            onChange={(e) => setTierId(e.target.value)}
          >
            <option value="">No ticket</option>
            {ticketTiers.map((tier) => (
              <option key={tier.id} value={tier.id}>
                {tier.name} — ${Number(tier.price).toFixed(2)}
              </option>
            ))}
          </select>
        </div>

        {/* Dietary Preferences */}
        <div style={modalStyles.fieldGroup}>
          <label style={modalStyles.label}>Dietary Preferences</label>
          <input
            type="text"
            style={modalStyles.input}
            value={dietaryPrefs}
            onChange={(e) => setDietaryPrefs(e.target.value)}
            maxLength={200}
            placeholder="e.g., Vegetarian, no nuts"
          />
        </div>

        {/* Chapter Affiliation */}
        <div style={modalStyles.fieldGroup}>
          <label style={modalStyles.label}>Chapter Affiliation</label>
          <select
            style={modalStyles.select}
            value={chapterAffiliation}
            onChange={(e) => setChapterAffiliation(e.target.value)}
          >
            <option value="">No chapter</option>
            {chapters.map((ch) => (
              <option key={ch.id} value={ch.name}>
                {ch.name}
              </option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div style={modalStyles.actions}>
          <button style={modalStyles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            style={saving ? modalStyles.saveBtnDisabled : modalStyles.saveBtn}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
